import { useEffect, useRef } from "react";
import { analytics } from "../../utils/segment/analyticsService";
import useMondayContext from "../context/useMondayContext";
import useBoardAndItems from "../context/useBoardAndItems";

/**
 * The SIMB surfaces we report load volume for, keyed by the
 * `appFeature.type` Monday hands us in its context.
 *
 * Deliberately keyed on appFeatureType rather than on the route: the router
 * path is not a reliable identifier of the surface. `home.tsx` redirects on
 * appFeatureType (see APP_FEATURE_ROUTES), so the item view and every other
 * surface mid-redirect all share the "/" path, and `/resynchronization` is
 * reachable both as the board view and as a modal opened from elsewhere.
 * The appFeature type is what Monday actually mounted us as.
 *
 * Surfaces absent from this map (AppFeatureBoardColumnExtension, the settings
 * modal, the onboarding screens) still emit page_loaded, just without a
 * view_type — they are loads we count but do not break out.
 */
const VIEW_TYPE_BY_APP_FEATURE: Record<string, string> = {
  AppFeatureItemView: "item_view",
  AppFeatureBoardView: "board_view",
  AppFeatureItemBatchAction: "batch_view",
  AppFeatureItemMenuAction: "board_item_menu",
  AppFeatureDialog: "boards_dialog",
};

const BATCH_VIEW = "batch_view";

export const PAGE_LOADED_EVENT = "page_loaded";

/**
 * Tracks the app surface opening, as Segment `page_loaded`.
 *
 * Sent through analyticsService, the single path every frontend event takes.
 * It goes to Segment and, during the Segment removal, also to Mixpanel
 * directly, so its shape is still reasoned about in one place.
 *
 * `view_type` rides on this existing event rather than becoming four new event
 * names (item_view_loaded, board_view_loaded, ...) or a second event of its
 * own. This hook already fires once per surface open — App mounts it above
 * <Routes> and each Monday surface is its own iframe boot — so it was already
 * a per-surface page-load event that simply never said which surface. A
 * parallel event would have double-counted every load in Mixpanel.
 */
export const usePageLoadAnalytics = (
  userId: number | null | undefined,
  accountId: number | null | undefined,
) => {
  const { appFeatureType, appVersion } = useMondayContext();
  const { currentBoard, itemIds } = useBoardAndItems();

  /**
   * Once per mounted app, i.e. once per surface open.
   *
   * A ref, not sessionStorage: sessionStorage would persist across iframe
   * boots and so would swallow a genuine second open of the same item in the
   * same tab, quietly undercounting the very thing this event measures. A ref
   * dies with the mount, which is exactly the boundary we want — it suppresses
   * only re-renders and repeat fetchMondayContext dispatches, whose fresh
   * `itemIds` array would otherwise re-fire the effect with identical data.
   */
  const hasTracked = useRef(false);

  const viewType = appFeatureType ? VIEW_TYPE_BY_APP_FEATURE[appFeatureType] : undefined;
  // Batch actions carry many selected items; a single id would misreport them.
  const itemId = viewType === BATCH_VIEW ? null : itemIds?.[0];

  useEffect(() => {
    if (!userId || hasTracked.current) return;
    hasTracked.current = true;

    analytics.track(PAGE_LOADED_EVENT, {
      groupId: accountId ? `${accountId}` : undefined,
      view_type: viewType,
      board_id: currentBoard ? `${currentBoard}` : undefined,
      item_id: itemId ? `${itemId}` : undefined,
      item_count: viewType === BATCH_VIEW ? (itemIds?.length ?? 0) : undefined,
      app_version: appVersion ?? undefined,
      platform: "web",
      timestamp: new Date().toISOString(),
    });
  }, [userId, accountId, viewType, currentBoard, itemId, itemIds, appVersion]);
};

export default usePageLoadAnalytics;
